/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    Address,
    AddressPurpose,
    IContext,
    CountryRegionInfo,
    StateProvinceInfo,
    // ProductDeliveryOptions,
    CartLine,
    FilterDeliveryModeOption,
    Cart,
    ProductDeliveryOptions
} from '@msdyn365-commerce/retail-proxy';
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
// import { getFallbackImageUrl, getSimpleProducts, ObjectExtensions, ProductInput } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, ITelemetryContent, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { action, computed, observable, reaction, set } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { CheckoutModulesStateShareInput } from '../../actions/checkout-modules-state-share';
import { vSIInitiateRestCallAsync } from '../../dotdigital-extension/DataActionExtension.g';
import { getSearchByCriteriaAsync, GetSearchByCriteriaInput } from '../../themes/dobbies/actions/search-by-criteria';
import { getRestrictedProducts } from '../../themes/dobbies/utilities/post-code-restriction/get-restricted-products';
import { AutoSuggest } from './common/address-autosuggest';
import { IAutoSuggestOptions } from './common/address-autosuggest.data';
import { AddressCommon } from './common/address-common';
import { AddressFormat } from './common/address-format';
import { AddressItemType } from './common/address-format.data';
import { AddressMetaData } from './common/address-meta-data';
import { AddressOperation, AddressType, IAddressResource, IAddressResponse } from './common/address-module.data';
import { AddressAddUpdate, IAddressAddUpdateProps } from './common/components/address-add';
import { AddressSelect, IAddressSelectProps } from './common/components/address-select';
import { AddressShow, IAddressShowProps } from './common/components/address-show';
import { IDobbiesCheckoutShippingAddressData } from './dobbies-checkout-shipping-address.data';
import { IDobbiesCheckoutShippingAddressProps } from './dobbies-checkout-shipping-address.props.autogenerated';
import { validateField } from '../../themes/dobbies/utilities/validation';
import { CheckoutAccordionStateInput } from '../../themes/dobbies/actions/checkout-accordion-steps-state';
import { getAddressSuggestionsAction, GetAddressSuggestionsInput } from '../../themes/dobbies/actions/get-address-suggestions';
import retrieveAddressAction, { RetrieveAddressInput } from '../../themes/dobbies/actions/retrieve-address';
import {
    getDeliveryOptionsAsync,
    updateLineDeliverySpecificationsAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { getDeliveryOptionsAsync as productsDataActionsDelOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import {
    getDeliveryOptionsForCartLineItems,
    ProductDeliveryOptionsForCartLineItems,
    QueryResultSettingsProxy
} from '@msdyn365-commerce-modules/retail-actions';
import { vSI_RemoveAllChargesAsync } from '../../vsi-add-charges-extension/DataActionExtension.g';
import { getCheckoutState } from '@msdyn365-commerce/global-state';
import { UpdateCheckoutShippingAddressInput } from '../../themes/dobbies/actions/update-shipping-address-again';
var axios = require('axios');

/**
 * The checkout address props interface.
 */
export interface ICheckoutAddressProps
    extends IDobbiesCheckoutShippingAddressProps<IDobbiesCheckoutShippingAddressData>,
        IModuleStateProps {}

/**
 * The checkout shipping address view state.
 */
export interface ICheckoutShippingAddressViewState {
    isShowAddress: boolean;
    isShowAddresList: boolean;
    isShowAddOrUpdateAddress: boolean;
    addressOptions: IPostCodeDropDownOption[];
    postalOptions: IPostCodeDropDownOption[];
    postCodeEmptyErrorMessage: string;
    productsRestrictedPostcodes: string[];
    /* VSI Customization - START - 15/04/21 */
    hasPlantsProducts?: boolean;
    isNotBtPostCode?: boolean;
    selectedAddressStreet?: string;
    showAddressSuggestionsBox?: boolean;
    /* VSI Customization - END */
    hasStartedTyping?: boolean;
}

/**
 * The checkout shipping address view props.
 */
export interface ICheckoutShippingAddressViewProps extends ICheckoutAddressProps {
    className: string;
    currentOperation: AddressOperation;
    selectedAddress?: Address;
    addUpdateAddress: Address;
    addressListSelectedAddress: Address;
    countryRegionId: string;
    stateProvinceInfo?: StateProvinceInfo[];
    customerAddresses: Address[];
    validationError: object;
    addressActionResponse?: IAddressResponse;
    viewState: ICheckoutShippingAddressViewState;
    CheckoutShippingAddress: IModuleProps;
    isUpdating: boolean;
    hasError: boolean;
    showAddress: IAddressShowProps;
    showAddressSelect: IAddressSelectProps;
    showAddOrUpdateAddress: IAddressAddUpdateProps;
    renderInputWithButton: React.ReactNode;
    postCodeEmptyErrorMessage: React.ReactNode;
    manualAddress: boolean;
    addAddress?: () => void;
    updateLoggedInAddress?: (shippingAddress: Address) => void;
    editAddressFromList?: (shippingAddress: Address) => void;
    shippingCost?: number;
    updateCartDeliverySpecification?: () => void;
}

/**
 * IExpressPaymentDetail interface.
 */
// interface IExpressPaymentDetails {
//     email?: string;
//     tenderLine?: TenderLine;
//     tokenizedPaymentCard?: TokenizedPaymentCard;
//     paymentTenderType?: string;
//     address?: Address;
//     isExpressCheckoutAppliedInCartPage: boolean;
//     isExpressAddressAppliedInCartPage?: boolean;
// }
export interface IDeliveryOption {
    organisation_name: string;
    department_name: string;
    line_1: string;
    line_2: string;
    udprn: string;
    dps: string;
}

export interface IPostCodeVerification {
    delivery_points: IDeliveryOption[];
    delivery_point_count: string;
    postal_county: string;
    traditional_county: string;
    town: string;
    postcode: string;
    error_code: string;
    error_msg: string;
}

export interface IAddressRetrieved {
    locality: string;
    line_1: string;
    line_2: string;
    postal_code: string;
}

export interface IPostCodeDropDownOption {
    option: string;
    postCode: IDeliveryOption;
    dps: string;
    town: string;
    code: string;
}
/**
 *
 * Address component.
 * @extends {React.Component<IDobbiesCheckoutShippingAddressProps<IDobbiesCheckoutShippingAddressData>>}
 */
// @ts-expect-error
@withModuleState
@observer
class CheckoutShippingAddress extends React.Component<ICheckoutAddressProps, ICheckoutShippingAddressViewState> {
    @observable private currentOperation: AddressOperation;

    @observable private selectedAddress?: Address;

    @observable private addUpdateAddress: Address;

    @observable private countryRegionId: string = 'USA';

    @observable private stateProvinceInfo?: StateProvinceInfo[];

    @observable private customerAddresses: Address[] = [];

    @observable private validationError: object;

    @observable private addressActionResponse?: IAddressResponse;

    @observable private isUpdating?: boolean;

    @observable private hasError?: boolean;

    @observable private addressListSelectedAddress: Address = {};

    private readonly addressCommon: AddressCommon;

    private addressFormat!: AddressFormat;

    private countryRegions: CountryRegionInfo[] = [];

    private addressPurposes: AddressPurpose[] = [];

    private readonly resources: IAddressResource;

    private readonly defaultAddressType: number = 6; // Default to Home

    private readonly telemetryContent?: ITelemetryContent;
    private postCodeInputRef: React.RefObject<HTMLInputElement>; // VSI Customization
    private autoSuggest?: AutoSuggest;

    private readonly multiplePickupStoreSwitchName: string = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';

    private retailMultiplePickUpOptionEnabled?: boolean = false;

    // private readonly checkoutErrorRef: React.RefObject<HTMLElement> = React.createRef();

    public constructor(props: ICheckoutAddressProps, state: ICheckoutShippingAddressViewState) {
        super(props);
        // this.state = { shippingGroups: [] };
        const { context, data, resources, telemetry } = this.props;

        this.addUpdateAddress = {};
        this.resources = resources;
        this.currentOperation = AddressOperation.List;
        this.countryRegions = data.countryRegions.result || [];
        this.addressPurposes = data.addressPurposes.result || [];
        this.customerAddresses = data.address.result || [];
        this.stateProvinceInfo = data.countryStates.result || [];
        this.addressCommon = new AddressCommon(context, resources, telemetry);
        this.addressFormat = new AddressFormat(
            this.countryRegions,
            new AddressMetaData({ ...resources }, this._getAddressFormatExcludeList()),
            this.addressPurposes
        );
        this.validationError = {};
        this.retailMultiplePickUpOptionEnabled = data.featureState.result?.find(
            feature => feature.Name === this.multiplePickupStoreSwitchName
        )?.IsEnabled;
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.postCodeInputRef = React.createRef<HTMLInputElement>();
        this.state = {
            postCodeEmptyErrorMessage: '',
            addressOptions: [],
            postalOptions: [],
            isShowAddresList: false,
            isShowAddress: false,
            isShowAddOrUpdateAddress: false,
            productsRestrictedPostcodes: [],
            showAddressSuggestionsBox: true
        };
    }

    public async componentDidMount(): Promise<void> {
        const {
            context: {
                telemetry,
                actionContext: {
                    requestContext: { channel }
                }
            },
            config: { autoSuggestionEnabled, autoSuggestOptions }
            // resources
        } = this.props;

        // Initializing data props
        // this._dataInitialize(this.props);

        // this.addressFormat = new AddressFormat(
        //     this.countryRegions,
        //     new AddressMetaData({ ...resources }, this._getAddressFormatExcludeList()),
        //     this.addressPurposes
        // );

        this.props.data.checkout.then(() => {
            this._setDefaultCountryRegionId();
            this._initModuleState();
        });

        reaction(
            () => this.countryRegionId,
            () => {
                this._getStateProvinces();
            }
        );

        reaction(
            () => this.currentOperation,
            () => {
                this._getStateProvinces();
            }
        );

        // reaction(
        //     () => this.props.data.checkout.result?.shippingAddressFromExpressCheckout,
        //     () => {
        //         if (!this.expressPaymentDetailsFromCartPage && this._canShip()) {
        //             let address = this.props.data.checkout.result?.shippingAddressFromExpressCheckout;

        //             if (address?.TwoLetterISORegionName) {
        //                 const threeLetterIsoRegionName = this.getThreeLetterIsoRegionName(address.TwoLetterISORegionName);

        //                 address = { ...address, ThreeLetterISORegionName: threeLetterIsoRegionName };

        //                 if (threeLetterIsoRegionName && threeLetterIsoRegionName !== this.countryRegionId) {
        //                     this._onCountryChange(threeLetterIsoRegionName);
        //                 }
        //             }

        //             this._updateCurrentOperation(AddressOperation.Add, address);
        //             this._onAddressAddUpdateSubmit();
        //         }
        //     }
        // );

        if (autoSuggestionEnabled) {
            if (channel && !channel.BingMapsApiKey) {
                telemetry.error('BingMapsApiKey is missing.');
                return;
            }

            if (channel && !channel.BingMapsEnabled) {
                telemetry.error('Map is disabled from HQ.');
                return;
            }

            const options: IAutoSuggestOptions = { ...autoSuggestOptions };
            this.autoSuggest = new AutoSuggest(
                telemetry,
                options,
                channel?.BingMapsApiKey,
                channel?.ChannelCountryRegionISOCode,
                channel?.DefaultLanguageId
            );

            // Customer doesn't have any address. Then add view will be loaded directly. Code for the same to handle that
            if (
                this.props.data.storeSelectorStateManager.result &&
                (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)
            ) {
                await this.autoSuggest?._loadMapAPI(await this.props.data.storeSelectorStateManager);
            }

            reaction(
                () =>
                    this.props.data.storeSelectorStateManager.result?.loadMapApi &&
                    (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update),
                async () => {
                    await this.autoSuggest?._loadMapAPI(await this.props.data.storeSelectorStateManager);
                }
            );

            reaction(
                () => this.props.data.storeSelectorStateManager.result?.isMapApiLoaded,
                async () => {
                    await this._attachMapAutoSuggest();
                }
            );
        }

        reaction(
            () => this.props.data.updateShippingAddressAgain.result?.updateShippingAddress,
            async () => {
                if (this.props.data.updateShippingAddressAgain.result?.updateShippingAddress) {
                    this.onEdit();
                    this.props.context.actionContext.update(new UpdateCheckoutShippingAddressInput(), { updateShippingAddress: false });
                }
            }
        );

        // if (this.props.data.checkout.result?.shouldEnableCheckoutErrorDisplayMessaging) {
        //     reaction(
        //         () => this.props.data.checkout.result?.checkoutError,
        //         checkoutError => {
        //             if (
        //                 checkoutError &&
        //                 checkoutError.errorLocation === ErrorLocation.CheckoutShippingAddress &&
        //                 checkoutError.errorMessage
        //             ) {
        //                 this._setErrorMessage(checkoutError.errorMessage);
        //             }
        //         }
        //     );

        //     reaction(
        //         () => this.props.data.checkout.result?.checkoutErrorFocus,
        //         checkoutErrorFocus => {
        //             if (checkoutErrorFocus === CheckoutModule.CheckoutShippingAddress) {
        //                 focusOnCheckoutError(this.checkoutErrorRef, this.props.context.actionContext);
        //             }
        //         }
        //     );
        // }

        // await this._initPickupGroup();

        document.addEventListener('click', (event: MouseEvent) => {
            const autoSuggestDiv = document.getElementById('auto-suggestions-div');
            const target = event.target as Node | null;
            if (!autoSuggestDiv?.contains(target)) {
                this.setState({ showAddressSuggestionsBox: false });
            }
        });
    }

    public async componentDidUpdate(): Promise<void> {
        if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
            if (this.props.data.storeSelectorStateManager.result?.isMapApiLoaded) {
                await this._attachMapAutoSuggest();
            }
        } else {
            // this.autoSuggest?.disposeAutoSuggest();
            const cls = this.props.data.cart.result?.cart.CartLines;
            let isOnlyCnc = true;
            if (Array.isArray(cls)) {
                for (const cl of cls) {
                    if (cl.DeliveryMode !== 'ClickCol') {
                        isOnlyCnc = false;
                    }
                }
            }
            if (!isOnlyCnc) {
                let addressOpened = false;
                const defaultAddressString = this.props.context.request.app.config.defaultGuestCustomerAddressString;
                if (defaultAddressString) {
                    const splittedDefaultAddress = defaultAddressString.split(',');
                    if (splittedDefaultAddress && splittedDefaultAddress.length >= 3) {
                        const defaultZip = splittedDefaultAddress[2];
                        if (defaultZip && (this.selectedAddress?.ZipCode === defaultZip || this.addUpdateAddress?.ZipCode === defaultZip)) {
                            this.selectedAddress = {};
                            this.addUpdateAddress = {};
                            this._goToAddAddress();
                            this._updateModuleState();
                            addressOpened = true;
                            return;
                        }
                    }
                }
                if (!this.selectedAddress?.Phone && !this.addUpdateAddress?.Phone && !addressOpened) {
                    this._goToAddAddress(true);
                } else {
                    this.autoSuggest?.disposeAutoSuggest();
                }
            } else {
                this.autoSuggest?.disposeAutoSuggest();
            }
        }
    }

    // public shouldComponentUpdate(nextProps: ICheckoutAddressProps, nextState: ICheckoutAddresState): boolean {
    //     if (this.state === nextState && this.props.data === nextProps.data) {
    //         return false;
    //     }
    //     return true;
    // }

    public render(): JSX.Element | null {
        if (!this.props.config.ignoreCanShip && !this._canShip()) {
            return null;
        }

        const { config, renderView } = this.props;
        // const { headingImages, itemsText, singleItemText, errorMessageTitle } = resources;
        // const { imageSettings } = config;

        // // Line images
        // const cartlines: ICheckoutShippingCartLineInfo[] = this.state.shippingGroups
        //     .filter(x => !ObjectExtensions.isNullOrUndefined(x.product))
        //     .map(line => ({
        //         lineId: line.cartLine.LineId ?? '',
        //         imageProps: {
        //             requestContext: context.actionContext.requestContext,
        //             className: 'ms-checkout-shipping-address__group-images-lines-line-image',
        //             altText: line.product?.Name,
        //             src: line.product?.PrimaryImageUrl ?? '',
        //             fallBackSrc: getFallbackImageUrl(line.product?.ItemId, context.actionContext.requestContext.apiSettings),
        //             gridSettings: context.request.gridSettings!,
        //             imageSettings: imageSettings ?? defaultImageSettings,
        //             loadFailureBehavior: 'empty'
        //         },
        //         quantity: line.cartLine.Quantity ?? 0
        //     }));

        // const quantity = 1;
        // const itemText = cartlines.length > quantity ? itemsText : singleItemText;

        // const lineImageProps: ICheckoutShippingCartLinesProps = {
        //     moduleClassName: 'ms-checkout-shipping-address',
        //     cartLines: cartlines.filter(x => !ObjectExtensions.isNullOrUndefined(x)),
        //     itemTitle: `(${format(itemText, cartlines.length)})`,
        //     title: headingImages,
        //     resources: this.props.resources
        // };

        // const cartLineImages: React.ReactNode = <CheckoutPickupCartLines {...lineImageProps} />;
        // const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        // const { featureState } = this.props.data;
        // const isRetailMultiplePickUpOptionEnabled = featureState.result?.find(feature => feature.Name === multiplePickupStoreSwitchName)
        //     ?.IsEnabled;

        // const errorMessage = this.state.errorMessage;

        const viewProps = {
            ...this.props,
            currentOperation: this.currentOperation,
            selectedAddress: this.selectedAddress,
            addUpdateAddress: this.addUpdateAddress,
            addressListSelectedAddress: this.addressListSelectedAddress,
            countryRegionId: this.countryRegionId,
            stateProvinceInfo: this.stateProvinceInfo,
            customerAddresses: this.customerAddresses,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            isUpdating: this.isUpdating,
            hasError: this.hasError,
            className: config.className,
            renderInputWithButton: undefined,
            postCodeEmptyErrorMessage: this._renderEmptyPostCodeErrorMessage(),
            viewState: {
                isShowAddress: this.currentOperation === AddressOperation.Show && this.selectedAddress,
                isShowAddresList: this.currentOperation === AddressOperation.List && this.customerAddresses.length > 0,
                isShowAddOrUpdateAddress:
                    this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update
            },
            CheckoutShippingAddress: {
                moduleProps: this.props,
                className: classnames('ms-checkout-shipping-address', config.className)
            },
            showAddress: this._renderShowAddress(),
            showAddressSelect: this._renderSelectAddress(),
            showAddOrUpdateAddress: this._renderAddOrUpdateAddress(),
            addAddress: this._goToAddAddress,
            updateLoggedInAddress: this._updateLoggedInAddress,
            editAddressFromList: this.editAddressFromList,
            updateCartDeliverySpecification: this.updateCartDeliverySpecification
            // alert: hasError && errorMessage && <ErrorComponent {...{ title: errorMessageTitle, message: errorMessage }} />,
            // checkoutErrorRef: this.checkoutErrorRef,

            // /**
            //  * Show address select.
            //  * @param onAddAddressHandler - On add address click function.
            //  * @param onSaveHandler - On save click function.
            //  * @param onCancelHandler - On cancel click function.
            //  * @returns - Renders select address.
            //  */
            // showAddressSelectHandler: (onAddAddressHandler?: () => void, onSaveHandler?: () => void, onCancelHandler?: () => void) =>
            //     this._renderSelectAddress(onAddAddressHandler, onSaveHandler, onCancelHandler),

            // /**
            //  * Show add/update address.
            //  * @param onSaveHandler - On save click function.
            //  * @param onCancelHandler - On cancel click function.
            //  * @returns - Renders select address.
            //  */
            // showAddOrUpdateAddressHandler: (onSaveHandler?: () => void, onCancelHandler?: () => void) =>
            //     this._renderAddOrUpdateAddress(onSaveHandler, onCancelHandler),
            // showAddOrUpdateAddress: this._renderAddOrUpdateAddress(),
            // cartLineImages: isRetailMultiplePickUpOptionEnabled ? cartLineImages : undefined,
            // showAddressSelect: this._renderSelectAddress()
        };

        return renderView(viewProps) as React.ReactElement;
    }
    /* VSI-Customization -- START When user selects option from autosuggestion box embed the selected option values into form fields  */
    private handleAddressOption = (option: IPostCodeDropDownOption) => (e: React.MouseEvent<HTMLDivElement>) => {
        const retrieveAddressInput: RetrieveAddressInput = new RetrieveAddressInput();
        retrieveAddressInput.addressId = option.code;
        retrieveAddressAction(retrieveAddressInput, this.props.context.actionContext)
            .then(res => {
                const addressOption: IPostCodeDropDownOption = {
                    postCode: { organisation_name: '', department_name: '', line_1: res.line_1, line_2: res.line_2, udprn: '', dps: '' },
                    option: res.line_1,
                    dps: '',
                    town: res.town,
                    code: res.postcode
                };
                this.setState({
                    addressOptions: [],
                    selectedAddressStreet: `${addressOption.postCode.line_1}, ${addressOption.postCode.line_2}`
                });
                if (addressOption) {
                    // populate values into form

                    this.onAddressAddUpdate('City', addressOption.town);
                    this.onAddressAddUpdate('Street', `${addressOption.postCode.line_1}, ${addressOption.postCode.line_2}`);
                    this.onAddressAddUpdate('ZipCode', addressOption.code);
                    // clear the postcode input field
                    // clear the array
                    this.postCodeInputRef.current!.value = '';
                    this.setState({ addressOptions: [] });
                } else {
                    this.onAddressAddUpdate('City', '');
                    this.onAddressAddUpdate('Street', '');
                    this.onAddressAddUpdate('ZipCode', '');
                }
            })
            .catch(error => {
                this.props.context.actionContext.telemetry.error(error);
            });
    };
    /* VSI-Customization -- END  */
    /* VSI-Customization -- START Created JSX based on options retrieved from API  */
    private _renderAddressOptions = (): React.ReactNode => {
        const { addressOptions } = this.state;
        if (addressOptions && addressOptions.length > 0) {
            return (
                <div>
                    {addressOptions.map((option: IPostCodeDropDownOption) => {
                        return (
                            <Node key={option.code} onClick={this.handleAddressOption(option)} className=''>
                                {option.option}
                            </Node>
                        );
                    })}
                </div>
            );
        }
        return;
    };
    /* VSI-Customization -- END  */
    /* VSI-Customization -- START If postcode is invalid display the error text message that please enter a valid postcode  */
    private _renderEmptyPostCodeErrorMessage = () => {
        return (
            <>
                {this.state.postCodeEmptyErrorMessage && (
                    <div className='post-code'>
                        <p className='post-code-error'>{this.state.postCodeEmptyErrorMessage}</p>
                    </div>
                )}
            </>
        );
    };
    /* VSI-Customization -- END  */
    /* VSI-Customization -- START When user enters postcode call the API and fetch the data  */

    /* VSI-Customization -- END  */
    private findPostalAddress = async (value: string) => {
        const errorMsg = validateField('ZipCode', value);
        if (!errorMsg) {
            const context: IContext = { callerContext: this.props.context.actionContext };
            const urlParams = {
                key: '1024c-01f0c-70dd6-06464',
                postcode: value,
                response: 'data_formatted',
                sort: 'asc'
            };

            const res = await vSIInitiateRestCallAsync(
                context,
                'Fetchify',
                'get',
                "{'cache-control':'no-cache' , 'Accept':'application/json'}",
                '',
                JSON.stringify(urlParams),
                `/json/rapidaddress`
            );
            if (res.result) {
                const responseResult: IPostCodeVerification = JSON.parse(res.result);
                /* VSI-Customization -- START If response got from API map it into array and update it with state (setState)  */
                if (!responseResult.error_msg) {
                    const postalOptions = this.mapPostCodeOptions(responseResult);
                    this.setState({ postalOptions: postalOptions, postCodeEmptyErrorMessage: '' });
                    /* VSI-Customization -- START If error code returns it means postcode isn't valid  */
                } else {
                    this.setState({ postalOptions: [] });
                }
            }
        } else if (value.length < 5) {
            this.setState({ postalOptions: [], postCodeEmptyErrorMessage: '' });
        }
    };
    /* VSI-Customization -- START Map response data into array and update state with mapped array  */
    private mapPostCodeOptions = (data: IPostCodeVerification) => {
        const addressOptions: IPostCodeDropDownOption[] = [];
        data &&
            data.delivery_points.map((option: IDeliveryOption) => {
                // create object and push it into array of type IPostCodeDropDownOption
                const obj: IPostCodeDropDownOption = {
                    option: `${option.line_1}, ${option.line_2}, ${data.town}`,
                    dps: option.dps,
                    postCode: option,
                    town: data.town,
                    code: data.postcode
                };
                addressOptions.push(obj);
            });
        return addressOptions;
    };
    /* VSI-Customization -- END  */
    /* VSI-Customization -- START Render input field with button  */
    private _renderInputWithButton = (): JSX.Element => {
        return (
            <>
                {this.state.addressOptions?.length > 0 && this.state.showAddressSuggestionsBox ? (
                    <div id='auto-suggestions-div' className='auto-suggestion'>
                        {this._renderAddressOptions()}
                    </div>
                ) : (
                    <></>
                )}
            </>
        );
    };
    /* VSI-Customization -- END  */

    // /**
    //  * Initialize pickup group.
    //  */
    // @action
    // private readonly _initPickupGroup = async (): Promise<void> => {
    //     const pickupCartLines: CartLine[] = this._getCartLinesforDelivery();
    //     const cartLines: ICartLineWithProduct[] = [];

    //     try {
    //         const products = await this._getProductsByCartLines(
    //             this.props.data.checkout.result?.checkoutCart.cart.ChannelId || 0,
    //             pickupCartLines
    //         );
    //         for (const line of pickupCartLines) {
    //             const product: SimpleProduct | undefined = products.find(x => x.RecordId === line.ProductId);
    //             cartLines.push({ cartLine: line, product });
    //         }
    //         this.setState({ shippingGroups: cartLines });
    //     } catch (error) {
    //         this.props.telemetry.error(error);
    //         this.setState({ shippingGroups: [] });
    //     }
    // };

    // /**
    //  * On suggestion selected.
    //  * @param result - Suggestion result interface.
    //  */
    // @action
    // private readonly _onSuggestionSelected = async (result: Microsoft.Maps.ISuggestionResult): Promise<void> => {
    //     this._clearAddressFields();
    //     const address = this.addressFormat.getTranformedAddress(result, this.stateProvinceInfo);
    //     const timeout = 0;
    //     set(this.addUpdateAddress, { Street: '' });
    //     set(this.addUpdateAddress, { ZipCode: address.ZipCode });
    //     set(this.addUpdateAddress, { CountyName: address.CountyName });
    //     set(this.addUpdateAddress, { City: address.City });
    //     set(this.addUpdateAddress, { State: address.State });
    //     set(this.addUpdateAddress, { DistrictName: address.DistrictName });
    //     set(this.addUpdateAddress, { FullAddress: address.FullAddress });

    //     // Bing autosuggest put the complete address in the Street input box. Updating the street input box to show only street address.
    //     setTimeout(() => {
    //         set(this.addUpdateAddress, { Street: address.Street });
    //     }, timeout);
    // };

    // /**
    //  * Method to clear address fields.
    //  */
    // @action
    // private readonly _clearAddressFields = (): void => {
    //     const addressFormatItem = this.addressFormat.getAddressFormat(
    //         this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId
    //     );
    //     for (const formatAddress of addressFormatItem) {
    //         if (
    //             this.addUpdateAddress[formatAddress.name] !== undefined &&
    //             !this.autoSuggest?.excludedAddressFields.includes(formatAddress.name)
    //         ) {
    //             this.addressFormat[formatAddress.name] = '';
    //         }
    //     }
    //     this._clearValidation();
    // };

    // /**
    //  * Method to clear validation.
    //  */
    // @action
    // private readonly _clearValidation = (): void => {
    //     this.validationError = {};
    // };

    // public get expressPaymentDetailsFromCartPage(): IExpressPaymentDetails | null {
    //     // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    //     const properties =
    //         this.props.data.cart?.result?.cart?.ExtensionProperties?.find(property => property.Key === 'expressPaymentDetails')?.Value
    //             ?.StringValue ?? '';

    //     return properties ? (JSON.parse(properties) as IExpressPaymentDetails) : null;
    // }

    // /**
    //  * Method data initialization.
    //  * @param props -The checkout address properties.
    //  */
    // private readonly _dataInitialize = (props: ICheckoutAddressProps): void => {
    //     const { data } = props;

    //     reaction(
    //         () => data.countryRegions.result,
    //         () => {
    //             this.countryRegions = data.countryRegions.result ?? [];
    //         }
    //     );

    //     reaction(
    //         () => data.addressPurposes.result,
    //         () => {
    //             this.addressPurposes = data.addressPurposes.result ?? [];
    //         }
    //     );

    //     reaction(
    //         () => data.address.result,
    //         () => {
    //             this.customerAddresses = data.address.result ?? [];
    //         }
    //     );

    //     reaction(
    //         () => data.countryStates.result,
    //         () => {
    //             this.stateProvinceInfo = data.countryStates.result ?? [];
    //         }
    //     );

    //     reaction(
    //         () => data.featureState.result,
    //         () => {
    //             this.retailMultiplePickUpOptionEnabled = data.featureState.result?.find(
    //                 feature => feature.Name === this.multiplePickupStoreSwitchName
    //             )?.IsEnabled;
    //         }
    //     );
    // };

    // /**
    //  * Method to get cart lines for delivery.
    //  * @returns The cart line collection.
    //  */
    // private readonly _getCartLinesforDelivery = (): CartLine[] => {
    //     return this.props.data.checkout.result?.checkoutCart.cart.CartLines?.filter(line => this._isDelivery(line)) ?? [];
    // };

    // /**
    //  * Method to check cart line for delivery.
    //  * @param line -The cart line.
    //  * @returns True/false as per cart line delivery mode.
    //  */
    // private readonly _isDelivery = (line: CartLine): boolean => {
    //     return this._isNotPickupMode(line.DeliveryMode) && (line.FulfillmentStoreId === undefined || line.FulfillmentStoreId === '');
    // };

    // /**
    //  * Method to check cart line for delivery.
    //  * @param deliveryMode -The delivery mode.
    //  * @returns True/false as per cart line delivery mode.
    //  */
    // private readonly _isNotPickupMode = (deliveryMode: string | undefined): boolean => {
    //     const pickupDeliveryModeCode = this.props.context.request.channel?.PickupDeliveryModeCode;
    //     const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
    //     const { channelDeliveryOptionConfig, featureState } = this.props.data;
    //     const retailMultiplePickUpOptionEnabled = featureState.result?.find(feature => feature.Name === multiplePickupStoreSwitchName)
    //         ?.IsEnabled;
    //     if (retailMultiplePickUpOptionEnabled && deliveryMode !== undefined) {
    //         const pickupDeliveryMode = channelDeliveryOptionConfig.result?.PickupDeliveryModeCodes?.some(
    //             pickupMode => pickupMode !== deliveryMode
    //         );
    //         return pickupDeliveryMode !== undefined ? pickupDeliveryMode : false;
    //     }
    //     return deliveryMode !== undefined && pickupDeliveryModeCode !== undefined && deliveryMode !== pickupDeliveryModeCode;
    // };

    // /**
    //  * Method to check cart line for delivery.
    //  * @param channelId - The channelId.
    //  * @param cartLines - Cart line collections.
    //  * @returns Collection of SimpleProduct.
    //  */
    // private readonly _getProductsByCartLines = async (channelId: number, cartLines: CartLine[]): Promise<SimpleProduct[]> => {
    //     const actionContext = this.props.context.actionContext;
    //     const productInputs = cartLines
    //         .filter(line => !ObjectExtensions.isNullOrUndefined(line.ProductId))
    //         .map(
    //             line =>
    //                 new ProductInput(
    //                     line.ProductId || 0,
    //                     actionContext.requestContext.apiSettings,
    //                     channelId,
    //                     undefined,
    //                     actionContext.requestContext
    //                 )
    //         );
    //     return getSimpleProducts(productInputs, actionContext);
    // };

    /**
     * Method to render add/update address.
     * @param onSaveHandler -- Handles onsave functionality.
     * @param onCancelHandler -- Handles oncancel functionality.
     * @returns Address app/update props.
     */
    private readonly _renderAddOrUpdateAddress = (onSaveHandler?: () => void, onCancelHandler?: () => void): IAddressAddUpdateProps => {
        const addressFormat =
            this.currentOperation === AddressOperation.Add && this.addressCommon.isAuthenticatedFlow()
                ? this.addressFormat
                : this.addressFormat;

        /**
         * On Cancel Button Function.
         */
        const onCancelButtonHandler = () => {
            this._resetView();
            onCancelHandler?.();
        };
        const validProductIds: number[] = [];
        this.props.data.cart.result?.cart.CartLines?.map(cl => {
            if (cl.ProductId) {
                validProductIds.push(cl.ProductId);
            }
        });
        return AddressAddUpdate({
            isUpdating: this.isUpdating,
            resources: this.resources,
            addressType: AddressType.Shipping,
            addressFormat: addressFormat.getAddressFormat(this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId),
            defaultCountryRegionId: this.countryRegionId,
            defaultAddressType: this.defaultAddressType,
            selectedAddress: this.addUpdateAddress,
            validationError: this.validationError,
            hasError: this.hasError,
            addressActionResponse: this.addressActionResponse,
            telemetryContent: this.telemetryContent,
            postalOptions: this.state.postalOptions || [],
            dropdownDisplayData: addressFormat.getPrefilledAddressDropdownData(
                this.resources.addressStateDefaultSelectionText,
                this.stateProvinceInfo
            ),
            onInputChange: this._onAddressAddUpdateInputChange,
            onDropdownChange: this._onAddressAddUpdateDropdownChange,
            hasExternalSubmitGroup: this.props.moduleState.hasExternalSubmitGroup,

            /**
             * On Save Function.
             */
            onSave: async () => {
                await this.onSubmit();
                onSaveHandler?.();
            },

            onCancel: !this.selectedAddress && !this.addressCommon.isAuthenticatedFlow() ? () => {} : onCancelButtonHandler,
            ctx: this.props.context.actionContext,
            productIds: validProductIds,
            hasStartedTypingEmail: this.state.hasStartedTyping,
            addressSuggestions: this._renderInputWithButton(),
            productsRestrictedPostcodes: this.state.productsRestrictedPostcodes
        });
    };

    /**
     * Method to render select address.
     * @param onAddAddressHandler - To handle add address button click.
     * @param onSaveHandler - To handle save button click.
     * @param onCancelHandler - To handle cancel button click.
     * @returns Select address props.
     */
    private readonly _renderSelectAddress = (
        onAddAddressHandler?: () => void,
        onSaveHandler?: () => void,
        onCancelHandler?: () => void
    ): IAddressSelectProps => {
        /**
         * On Cancel Button Function.
         */
        const onCancelButtonHandler = () => {
            this._resetView();
            onCancelHandler?.();
        };
        return AddressSelect({
            addressFormat: this.addressFormat,
            addresses: this.customerAddresses,
            resources: this.resources,
            addressPurposes: this.addressPurposes,
            selectedAddress: this.addressListSelectedAddress,
            onAddressOptionChange: this._onAddressOptionChange,
            hasExternalSubmitGroup: this.props.moduleState.hasExternalSubmitGroup,
            telemetryContent: this.telemetryContent,

            /**
             * On Add Address Function.
             */
            onAddAddress: () => {
                this._goToAddAddress();
                onAddAddressHandler?.();
            },

            /**
             * On Save Function.
             */
            onSave: () => {
                this._onSelectAddress();
                onSaveHandler?.();
            },

            onCancel: !this.selectedAddress ? () => {} : onCancelButtonHandler
        });
    };

    /**
     * Method to render show address.
     * @returns Show address props.
     */
    private _renderShowAddress(): IAddressShowProps | null {
        if (this.selectedAddress) {
            return AddressShow({
                address: this.selectedAddress,
                addressFormat: this.addressFormat.getAddressFormat(this.selectedAddress.ThreeLetterISORegionName || ''),
                addressPurposes: this.addressPurposes
            });
        }

        return null;
    }

    /**
     * Method to render map auto suggest.
     */
    private readonly _attachMapAutoSuggest = async (): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (storeSelectorStateManager?.isMapApiLoaded) {
            this.autoSuggest?.attachAutoSuggest('#shipping_addressstreet', '#shipping_addressstreet_container', this._onSuggestionSelected);
        }
    };
    @action
    private _onSuggestionSelected = async (result: Microsoft.Maps.ISuggestionResult): Promise<void> => {
        this._clearAddressFields();
        const address = this.addressFormat.getTranformedAddress(result, this.stateProvinceInfo);
        set(this.addUpdateAddress, { ['Street']: '' });
        set(this.addUpdateAddress, { ['ZipCode']: address.ZipCode });
        set(this.addUpdateAddress, { ['CountyName']: address.CountyName });
        set(this.addUpdateAddress, { ['City']: address.City });
        set(this.addUpdateAddress, { ['State']: address.State });
        set(this.addUpdateAddress, { ['DistrictName']: address.DistrictName });
        set(this.addUpdateAddress, { ['FullAddress']: address.FullAddress });
        // Bing autosuggest put the complete address in the Street input box. Updating the street input box to show only street address.
        setTimeout(() => {
            set(this.addUpdateAddress, { ['Street']: address.Street });
        }, 0);
    };
    @action
    private _clearAddressFields = (): void => {
        const addressFormatItem = this.addressFormat.getAddressFormat(
            this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId
        );

        addressFormatItem.forEach(format => {
            const fieldName = format.name as keyof Address;

            if (this.addUpdateAddress[fieldName] !== undefined && !this.autoSuggest?.excludedAddressFields.includes(format.name)) {
                (this.addUpdateAddress as Record<keyof Address, string | undefined>)[fieldName] = '';
            }
        });

        this._clearValidation();
    };

    @action
    private _clearValidation = (): void => {
        this.validationError = {};
    };

    /**
     * Method gets called on address option change.
     * @param event - To get current option.
     */
    private readonly _onAddressOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const addressRecordId = event.currentTarget.value;
        let selectedAddress = this.customerAddresses.find(address => (address.RecordId || '').toString() === addressRecordId);
        /* VSI Customization - START - 15/04/21 */
        // Check if cart has any plant product, user must enter BT postcode otherwise error message will be shown
        const isOtherPostcode = selectedAddress && this._isRestrictedPostCode(selectedAddress);
        this.setState({ isNotBtPostCode: isOtherPostcode });
        this._updateCheckoutSharedState(
            !!isOtherPostcode,
            !!this.props.data.checkoutModulesSharedState.result?.invalidAddressForCartProducts
        );

        /* VSI Customization - END */
        if (selectedAddress) {
            selectedAddress = {
                ...selectedAddress,
                Email:
                    selectedAddress.Email && selectedAddress.Email !== ''
                        ? selectedAddress.Email
                        : this.props.data.customerInformation.result?.Email
            };
            this.addressListSelectedAddress = selectedAddress;
        }
    };

    /**
     * Method to render map auto suggest.
     * @param name - Address name field.
     * @param value - Address name value field.
     */
    private readonly onAddressAddUpdate = (name: string, value: string) => {
        if (name === 'ZipCode') {
            void this.findPostalAddress(value);
        }
        if (name === 'Street' && value !== this.state.selectedAddressStreet) {
            const addressSuggestionInput = new GetAddressSuggestionsInput();
            addressSuggestionInput.query = value;
            getAddressSuggestionsAction(addressSuggestionInput, this.props.context.actionContext).then(suggestions => {
                const addressOpts: IPostCodeDropDownOption[] = [];
                for (const suggestion of suggestions) {
                    const suggestionZip = suggestion.labels[0];
                    const suggestionStreetDetails = suggestion.labels[1];
                    const splittedStreetAddr = suggestionStreetDetails.split(',');
                    let streetAddr = '';
                    let town = '';
                    if (splittedStreetAddr.length > 1) {
                        streetAddr = splittedStreetAddr[0];
                        town = splittedStreetAddr[1];
                    }
                    addressOpts.push({
                        code: suggestion.id,
                        option: suggestionStreetDetails,
                        town: town,
                        postCode: {
                            line_1: streetAddr,
                            department_name: '',
                            line_2: '',
                            organisation_name: '',
                            udprn: '',
                            dps: suggestionZip
                        },
                        dps: suggestionZip
                    });
                }
                this.setState({ addressOptions: addressOpts, showAddressSuggestionsBox: true });
            });
        }
        set(this.addUpdateAddress, { [name]: value });
        this.addressFormat.validateAddressFormat(
            this.addUpdateAddress,
            this.validationError,
            this.countryRegionId,
            name,
            this.state.productsRestrictedPostcodes
        );
    };

    /**
     * Method to get called on address update change.
     * @param event - Input element.
     */
    private readonly _onAddressAddUpdateInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        // if (event.target.type === 'checkbox') {
        //     this.onAddressAddUpdate(event.target.name, event.target.checked);
        // } else {
        if (event.target.name.toLowerCase() === 'email' && !this.state.hasStartedTyping) {
            this.setState({
                hasStartedTyping: true
            });
        }

        const value = (event.target.value || '').replace(new RegExp('[<>]', 'gi'), '');
        this.onAddressAddUpdate(event.target.name, value);
        // }
    };

    /**
     * Method to get called on address update dropdown change.
     * @param event - Select element.
     */
    private readonly _onAddressAddUpdateDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        this.onAddressAddUpdate(event.target.name, event.target.value);

        if (event.target.name === AddressItemType[AddressItemType.ThreeLetterISORegionName]) {
            this._onCountryChange(event.target.value);
        }
    };

    /**
     * Method to get called on address update submit.
     */
    private readonly _onAddressAddUpdateSubmit = async (): Promise<void> => {
        if (
            !this.addressFormat.validateAddressFormat(
                this.addUpdateAddress,
                this.validationError,
                this.countryRegionId,
                undefined,
                this.state.productsRestrictedPostcodes
            )
        ) {
            return;
        }
        // console.log('postalOptions', this.state.postalOptions)
        // if (this.state.postalOptions && this.state.postalOptions.length <= 0) {
        //     return;
        // }
        let response: Promise<IAddressResponse>;
        if (this.addressCommon.isAuthenticatedFlow()) {
            /* VSI Customization - START - 15/04/21 */
            // Check if cart has any plant product, user must enter BT postcode otherwise error message will be shown
            const isOtherPostCode = this.addUpdateAddress && this._isRestrictedPostCode(this.addUpdateAddress);
            this.setState({ isNotBtPostCode: isOtherPostCode });
            // Check if there is any address saved or user will enter his first address, so confirm button will not be disabled and just error message will be shown
            const isFirstAddress = this.customerAddresses.length === 0;
            // Last argument is used to check if address is entered for second time, shipping address button will not be disabled but only delivery option
            this._updateCheckoutSharedState(!!isOtherPostCode, !!isOtherPostCode, isFirstAddress, !isFirstAddress);
            // Check if it is BelFast's postcode that starts with BT
            if (isOtherPostCode) {
                return;
            }
            /* VSI Customization - END */
            response =
                this.currentOperation === AddressOperation.Update
                    ? this.addressCommon.updateCustomerAddress(this.addUpdateAddress)
                    : this.addressCommon.addCustomerAddress(this.addUpdateAddress);
        } else {
            /* VSI Customization - START - 15/04/21 */
            // Check if cart has any plant product, user must enter BT postcode otherwise error message will be shown to guest user
            const isOtherPostCode = this.addUpdateAddress && this._isRestrictedPostCode(this.addUpdateAddress);
            this.setState({ isNotBtPostCode: isOtherPostCode });
            this._updateCheckoutSharedState(
                !!isOtherPostCode,
                !!this.props.data.checkoutModulesSharedState.result?.invalidAddressForCartProducts,
                true
            );
            // this._updateCheckoutSharedState(!!this.props.data.checkoutModulesSharedState.result?.notBtPostCode, !!isOtherPostCode);
            // Check if it is BelFast's postcode that starts with BT
            if (isOtherPostCode) {
                return;
            }
            /* VSI Customization - END */
            response = Promise.resolve({ address: this.addUpdateAddress });
        }

        this.isUpdating = true;
        try {
            const result: IAddressResponse = await response;
            this.isUpdating = false;

            // let newAddress = result.address;
            // newAddress = { ...newAddress, ...this.addUpdateAddress };

            if (result.address) {
                this.hasError = false;
                if (this.addUpdateAddress.Email) {
                    await this.props.data.checkout.result?.checkoutCart.updateReceiptEmail({ newEmail: this.addUpdateAddress.Email });
                    await this.props.data.checkout.result?.updateGuestCheckoutEmail({
                        newGuestCheckoutEmail: this.addUpdateAddress.Email
                    });
                } else if (this.selectedAddress?.Email) {
                    await this.props.data.checkout.result?.checkoutCart.updateReceiptEmail({ newEmail: this.selectedAddress.Email });
                    await this.props.data.checkout.result?.updateGuestCheckoutEmail({
                        newGuestCheckoutEmail: this.selectedAddress.Email
                    });
                }
                await this._onAddOrUpdateSuccess(result);
                await this.props.data.checkout.result?.updateShippingAddress({ newShippingAddress: result.address });
                await this.updateCartDeliverySpecification();
                // this._clearError();
                // if (result.customerAddresses) {
                //     this._onAddOrUpdateSuccess({ customerAddresses: result.customerAddresses, address: newAddress });
                // } else {
                //     this._onAddOrUpdateSuccess({ address: newAddress });
                // }
            } else {
                this.hasError = true;
                this.addressActionResponse = result;
            }
            this._updateModuleState();
        } catch (error) {
            this.hasError = true;
            this.isUpdating = false;
            this._updateModuleState();
        }
    };

    /**
     * Method to get called on country change.
     * @param countryRegionId - Country region id.
     */
    private readonly _onCountryChange = (countryRegionId: string) => {
        this.countryRegionId = countryRegionId;
        const twoLetterISORegionName = this.addressFormat.getTwoLetterISORegionName(countryRegionId);
        set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: countryRegionId });
        set(this.addUpdateAddress, { ['TwoLetterISORegionName']: twoLetterISORegionName });
        this.autoSuggest?.changeAutoSuggestionCountryCode(twoLetterISORegionName);
        this._clearAddressFields();
    };

    /**
     * Method to get all address format exclude list.
     * @returns Collection of address items.
     */
    private readonly _getAddressFormatExcludeList = (): AddressItemType[] => {
        const { config } = this.props;
        const addressFormatExcludeList: AddressItemType[] = [];

        if (!config.showAddressType) {
            addressFormatExcludeList.push(AddressItemType.AddressTypeValue);
        }

        addressFormatExcludeList.push(AddressItemType.IsPrimary);

        return addressFormatExcludeList;
    };

    /**
     * Method to get all state/provinces.
     */
    private readonly _getStateProvinces = (): void => {
        if (
            !this.countryRegionId ||
            !(this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)
        ) {
            return;
        }

        this.addressCommon.getStateProvinces(this.countryRegionId).then((result: StateProvinceInfo[]) => {
            const stateInfo = result.some(state => state.StateId === this.addUpdateAddress.State);

            // Reset state if selected state not found in the list.
            if (!stateInfo) {
                set(this.addUpdateAddress, { ['State']: '' });
            }

            this.stateProvinceInfo = result;
        });
    };

    /**
     * Method to set default country region id.
     */
    private _setDefaultCountryRegionId(): void {
        const { request } = this.props.context;
        const market = request.channel?.ChannelCountryRegionISOCode;
        this.countryRegionId = this.addressCommon.getDefaultCountryRegionId(this.countryRegionId, this.countryRegions, market);
    }

    /**
     * Method to get default address region id.
     * @returns - Address object from existing addresses.
     */
    private readonly _getDefaultAddress = (): Address | undefined => {
        if (this.customerAddresses) {
            const primaryAddress = this.customerAddresses.find((address: Address) => address.IsPrimary);
            let defaultCustomerAddress = primaryAddress || (this.customerAddresses.length > 0 ? this.customerAddresses[0] : undefined);
            if ((defaultCustomerAddress && !defaultCustomerAddress?.Email) || defaultCustomerAddress?.Email === '') {
                defaultCustomerAddress = { ...defaultCustomerAddress, Email: this.props.data.customerInformation.result?.Email };
            }

            return defaultCustomerAddress;
        }
        return undefined;
    };

    /**
     * Method to get address from express payment details from cart page.
     * @returns - Address.
     */

    /**
     * Method to initialize all module state.
     */
    private readonly _initModuleState = async (): Promise<void> => {
        // Checks post code restriction on page loading when user's default address is selected

        this._isPostCodeRestricted();
        /* VSI Customization - END */
        this.props.moduleState.init({
            status: this._canShip() ? 'updating' : 'disabled',
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit
        });

        const checkoutState = this.props.data.checkout.result;

        if (this._canShip()) {
            // If cart line has default address then autopopulate address for logged in user
            const cartLineAddress = this._getShippingAddressFromCartLines();
            const customerAddress = this._getDefaultAddress();
            let defaultAddress = cartLineAddress;
            // if name is empty then it is default address
            if ((cartLineAddress?.Name === '' || cartLineAddress?.Email === '') && customerAddress) {
                defaultAddress = customerAddress;
            }
            ///// Added from Update to 44
            const checkoutCartAddressIsNew = false;
            const userIsAuthenticated = this.addressCommon.isAuthenticatedFlow();

            if (
                this.props.config.forceReselectShippingAddressOnDeletedSavedAddressEnabled &&
                userIsAuthenticated &&
                checkoutCartAddressIsNew
            ) {
                if (this.customerAddresses.length > 0) {
                    // Have the user choose a new address from their list of saved addresses
                    this._updateCurrentOperation(AddressOperation.List);
                    checkoutState?.updateHasShippingAddress({ newHasShippingAddress: false });
                } else {
                    // Have the user create a new address if there are no other saved addresses
                    this._updateCurrentOperation(AddressOperation.Add);
                    checkoutState?.updateHasShippingAddress({ newHasShippingAddress: false });
                }
            } else if (defaultAddress && !this.addressCommon.isEmpty(defaultAddress)) {
                this._updateCurrentOperation(AddressOperation.Show, defaultAddress);
                await this._setShippingAddress(defaultAddress);
                this._updateModuleState();
                // checkoutState?.updateHasShippingAddress({ newHasShippingAddress: true });
            } else {
                this._updateCurrentOperation(AddressOperation.Add);
                // checkoutState?.updateHasShippingAddress({ newHasShippingAddress: false });
            }
        }
    };

    private removeAllCharges = async () => {
        const cartState = this.props.data.checkout.result?.checkoutCart;
        const cartId = (cartState?.cart as Cart).Id;
        if (cartId) {
            await vSI_RemoveAllChargesAsync({ callerContext: this.props.context.actionContext }, cartId);
        }
    };

    private readonly updateCartDeliverySpecification = async (): Promise<void> => {
        try {
            const { context, data } = this.props;

            const checkoutCart = data.checkout.result?.checkoutCart;
            const cart = data.cart.result?.cart;
            const customerInfo = data.customerInformation.result;

            if (!checkoutCart || !cart) {
                return;
            }

            const delOptionsForCart = await getDeliveryOptionsAsync(
                {
                    callerContext: context.actionContext,
                    queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context.actionContext)
                },
                checkoutCart.cart.Id ?? '',
                data.checkout.result?.shippingAddress,
                [],
                FilterDeliveryModeOption.FilterOutNonCarrierDeliveryMode,
                false
            );

            const deliveryOptions: ProductDeliveryOptions[] = await getDeliveryOptionsForCartLineItems(
                new ProductDeliveryOptionsForCartLineItems(context.request.apiSettings),
                context.actionContext
            );

            // ---- Resolve Shipping Address ----
            const defaultAddress = context.request.app.config.defaultGuestCustomerAddressString.split(',');
            if (defaultAddress.length < 3) {
                return;
            }

            let shippingAddress: Address = {
                Street: defaultAddress[0],
                City: defaultAddress[1],
                ZipCode: defaultAddress[2],
                ThreeLetterISORegionName: 'GBR'
            };

            if (context.request.user.isAuthenticated && customerInfo?.Addresses?.length) {
                shippingAddress = customerInfo.Addresses[0];
            }

            if (data.checkout.result?.shippingAddress) {
                shippingAddress = data.checkout.result.shippingAddress;
            }

            // ---- Delivery Mode Logic ----
            const homeDeliveryModes = ['STD', 'STD-2', 'HD', 'PF', 'HD Presale', 'DTC-05days', 'DTC-10days', 'DTC-14days', 'DTC-28days'];

            let lastHomeMode = '';
            let hasMultiHomeModes = false;

            cart.CartLines?.forEach((cartLine: CartLine) => {
                const temp = deliveryOptions.find(o => o.ProductId === cartLine.ProductId);
                if (temp && temp.DeliveryOptions) {
                    const mode = temp.DeliveryOptions.find(o => o.Code !== 'Common MOD' && o.Code !== 'ClickCol');
                    if (mode && mode.Code) {
                        if (!lastHomeMode) {
                            lastHomeMode = mode.Code;
                        }
                        if (homeDeliveryModes.includes(mode.Code) && mode.Code !== lastHomeMode) {
                            hasMultiHomeModes = true;
                        }
                    }
                }
            });

            // ---- Flatten shipping lines ----
            const shippingCartLines = cart.CartLines?.filter(cl => !cl.FulfillmentStoreId);
            if (!shippingCartLines?.length) {
                return;
            }

            // ---- Delivery Option Selection ----
            const getSelectedOption = () => {
                if (hasMultiHomeModes) {
                    return delOptionsForCart.find(d => d.Code === 'Common MOD');
                }
                return delOptionsForCart.find(d => d.Code !== 'ClickCol' && d.Code !== 'Common MOD');
            };

            const selectedOption = getSelectedOption();

            const deliverySpecifications = shippingCartLines.map((cl: CartLine) => ({
                LineId: cl.LineId,
                DeliverySpecification: {
                    DeliveryModeId: cl.FulfillmentStoreId ? 'ClickCol' : selectedOption?.Code,
                    DeliveryPreferenceTypeValue: 1,
                    DeliveryAddress: shippingAddress
                }
            }));

            // ---- Resolve Product Delivery Options ----
            const productIds = shippingCartLines.map(cl => cl.ProductId).filter((id): id is number => id !== undefined);

            await productsDataActionsDelOptionsAsync({ callerContext: context.actionContext, queryResultSettings: {} }, productIds);

            // ---- Handle STD Charges ----
            const checkoutState = await getCheckoutState(context.actionContext);
            const hasSTDCharges = checkoutState.checkoutCart.cart.ChargeLines?.some(
                c => c.IsShipping && c.CalculatedAmount === context.app.config.chargesForHomeDeliveryItems
            );

            if (hasSTDCharges) {
                await this.removeAllCharges();
            }

            await checkoutCart.updateShippingAddress({ newShippingAddress: {} });

            await updateLineDeliverySpecificationsAsync(
                { callerContext: context.actionContext },
                checkoutCart.cart.Id ?? '',
                deliverySpecifications
            );

            await checkoutCart.refreshCart({});
        } catch (err) {
            console.error('Error calling VSI add charge', err);
        }
    };

    /**
     * Method get called on submit address.
     */
    private readonly onSubmit = async (): Promise<void> => {
        localStorage.setItem('savedAddress', JSON.stringify(this.addUpdateAddress));
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                // this.props.telemetry.information('Shipping address submit for Add or Update operation.');
                await this._onAddressAddUpdateSubmit();
                this.props.context.actionContext.requestContext.cookies.set('entered_zip_code', this.addUpdateAddress.ZipCode);

                break;
            case AddressOperation.List:
                // this.props.telemetry.information('Shipping address submit for List operation.');
                await this._onSelectAddress();
                this.props.context.actionContext.requestContext.cookies.set('entered_zip_code', this.addressListSelectedAddress.ZipCode);
                break;
            default:
                this.props.telemetry.error('Invalid operation');
        }
    };

    /**
     * Method get called on cancel.
     */
    private readonly onCancel = (): void => {
        /* VSI Customization - START - 16/04/21
           when user has already selected an address but tries to select another address with postcode other than BT
           Disable the confirm & proceed button  */
        if (this.selectedAddress && this._isRestrictedPostCode(this.selectedAddress)) {
            this._updateCheckoutSharedState(true, true);
        } else {
            this._updateCheckoutSharedState(false, false);
        }
        /* VSI Customization - END */
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                this.props.telemetry.information('Shipping address cancel for Add or Update operation.');
                this._clearAddressFields();
                if (!(!this.selectedAddress && !this.addressCommon.isAuthenticatedFlow())) {
                    /* VSI Customization - START - 16/04/21
                    when user has already selected an address but tries to add new address with postcode other than BT
                    Disable the confirm & proceed button  */
                    if (this.shippingAddress && this._isRestrictedPostCode(this.shippingAddress)) {
                        this._updateCheckoutSharedState(true, true);
                    } else {
                        this._updateCheckoutSharedState(false, false);
                    }
                    /* VSI Customization - END */
                    this._resetView();
                }

                break;
            case AddressOperation.List:
                this.props.telemetry.information('Shipping address cancel for List operation.');
                if (this.selectedAddress) {
                    this._resetView();
                }
                break;
            default:
                this.props.telemetry.error('Invalid operation');
        }
    };

    /**
     * Method get called on edit address.
     */
    private readonly onEdit = (): void => {
        // this.props.telemetry.information('Shipping address onEdit is called.');

        if (this.addressCommon.isAuthenticatedFlow() && this.shippingAddress) {
            const updatedShippingAddress = {
                ...this.shippingAddress,
                Email:
                    this.shippingAddress.Email && this.shippingAddress.Email !== ''
                        ? this.shippingAddress.Email
                        : this.props.data.customerInformation.result?.Email
            };
            this._updateCurrentOperation(AddressOperation.List, updatedShippingAddress);
        } else if (this.shippingAddress) {
            this._updateCurrentOperation(AddressOperation.Update, this.shippingAddress);
        }

        this._updateModuleState();
        this.props.context.actionContext.update(new CheckoutAccordionStateInput(), { currentStep: 1 });
        // if (this.shippingAddress) {
        //     this._updateCurrentOperation(operation, operation === AddressOperation.Add ?undefined : this.shippingAddress);
        //     this._updateModuleState();
        // }
    };
    private _updateLoggedInAddress = (shippingAddress: Address): void => {
        this._updateCurrentOperation(AddressOperation.Update, shippingAddress);
    };
    private _getShippingAddressFromCartLines = (): Address | undefined => {
        const { request } = this.props.context;
        const { channelDeliveryOptionConfig } = this.props.data;
        const pickupDeliveryModeCode = request && request.channel && request.channel.PickupDeliveryModeCode;

        const emailDeliveryModeCode = request && request.channel && request.channel.EmailDeliveryModeCode;
        const cartLines = this.retailMultiplePickUpOptionEnabled
            ? ((this.props.data.checkout.result && this.props.data.checkout.result.checkoutCart.cart.CartLines) || []).filter(cartLine =>
                  cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                      ? cartLine.DeliveryMode !==
                            channelDeliveryOptionConfig.result?.PickupDeliveryModeCodes?.find(
                                deliveryMode => deliveryMode === cartLine.DeliveryMode
                            ) && cartLine.DeliveryMode !== emailDeliveryModeCode
                      : cartLine
              )
            : ((this.props.data.checkout.result && this.props.data.checkout.result.checkoutCart.cart.CartLines) || []).filter(cartLine =>
                  cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                      ? cartLine.DeliveryMode !== pickupDeliveryModeCode && cartLine.DeliveryMode !== emailDeliveryModeCode
                      : cartLine
              );

        if (cartLines && cartLines.length > 0) {
            return cartLines[0].ShippingAddress;
        }

        return undefined;
    };

    /**
     * Method to check if checkout cartlines are available fro shipping.
     * @returns - True/false as per the delivery mode.
     */
    private readonly _canShip = (): boolean => {
        const { checkout, channelDeliveryOptionConfig } = this.props.data;
        const { request } = this.props.context;
        const pickupDeliveryModeCode = request && request.channel && request.channel.PickupDeliveryModeCode;
        const emailDeliveryModeCode = request && request.channel && request.channel.EmailDeliveryModeCode;

        if (!checkout.result || !request.channel || checkout.result.checkoutCart.isEmpty || checkout.result.checkoutCart.hasInvoiceLine) {
            return false;
        }

        return (
            (this.retailMultiplePickUpOptionEnabled
                ? checkout.result.checkoutCart.cart.CartLines?.some(cartLine =>
                      cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                          ? cartLine.DeliveryMode !==
                                channelDeliveryOptionConfig.result?.PickupDeliveryModeCodes?.find(
                                    deliveryMode => deliveryMode === cartLine.DeliveryMode
                                ) && cartLine.DeliveryMode !== emailDeliveryModeCode
                          : cartLine
                  )
                : checkout.result.checkoutCart.cart.CartLines?.some(cartLine =>
                      cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                          ? cartLine.DeliveryMode !== pickupDeliveryModeCode && cartLine.DeliveryMode !== emailDeliveryModeCode
                          : cartLine
                  )) ?? false
        );
    };
    /* VSI Customization - START - 15/04/21 */
    /* Following function checks if user's selected address is not BT & cart has any plants product, display error message on loading checkout */
    private _isPostCodeRestricted = () => {
        const {
            data: {
                checkout: { result: checkout }
            },
            context: { actionContext, telemetry }
        } = this.props;
        // Check if product's category is plants

        const productIds: number[] = [];
        checkout?.checkoutCart?.cart.CartLines?.map(cartLine => {
            cartLine.ProductId && productIds.push(cartLine.ProductId);
        });

        const actionInput: GetSearchByCriteriaInput = {
            productIds: productIds,
            context: actionContext,
            dataCacheType: () => 'request',
            getCacheKey: () => 'postCodeRestriction',
            getCacheObjectType: () => 'postCodeRestriction'
        };

        const postCodeRestrictions: string[] = [];
        getSearchByCriteriaAsync(actionInput, actionContext)
            .then(async productsSearchResult => {
                for (const val of productsSearchResult) {
                    const productAttributes = await this._getRestrictedPostCodes(
                        val.RecordId,
                        actionContext.requestContext.apiSettings.baseUrl
                    );
                    productAttributes.forEach((attr: any) => {
                        if (attr?.Name && attr.Name === 'RestrictedPostCode' && attr.TextValue) {
                            const postcodesTemp = JSON.parse(attr?.TextValue).RestrictedPostCode;
                            postcodesTemp.forEach((postcode: string) => postCodeRestrictions.push(postcode));
                        }
                    });
                }

                // VSI Customization  - 22/02/22 - Setting state as set so that we don't have duplicates to check
                this.setState({
                    productsRestrictedPostcodes: [...new Set(postCodeRestrictions)]
                });

                getRestrictedProducts(
                    productsSearchResult,
                    this.props.context.actionContext.requestContext.apiSettings.baseUrl,
                    this.props.context.actionContext
                );
                const isOtherPostcode = !!(checkout?.shippingAddress && this._isRestrictedPostCode(checkout.shippingAddress));

                this.setState({ hasPlantsProducts: true });
                this._updateCheckoutSharedState(isOtherPostcode, isOtherPostcode);
            })
            .catch(error => telemetry.trace(error));
    };
    /* Following function returns true if zipCode has value starting from other than BT */
    private _isRestrictedPostCode = (address: Address) => {
        const zipCode = address && address.ZipCode ? address.ZipCode?.toLowerCase() : '';

        const restrictedRegions: string[] = this.state.productsRestrictedPostcodes;
        const isRestrictedPostCode =
            restrictedRegions &&
            restrictedRegions.find(postCode => {
                return zipCode.startsWith(postCode.toLowerCase());
            });
        if (isRestrictedPostCode) {
            return true;
        }
        return false;
    };

    public _getRestrictedPostCodes = (productId: number, attributesBaseUrl?: string): any => {
        const url = attributesBaseUrl
            ? `${attributesBaseUrl}Commerce/Products(${productId})/GetAttributeValues(channelId=5637147577,catalogId=0)?$top=1000&api-version=7.3`
            : `https://scu40oznal298244666-rs.su.retail.dynamics.com/Commerce/Products(${productId})/GetAttributeValues(channelId=5637147577,catalogId=0)?$top=1000&api-version=7.3`;
        var config = {
            method: 'get',
            url: url,
            headers: {
                authority: attributesBaseUrl ? attributesBaseUrl.split('://')[1] : 'scu40oznal298244666-rs.su.retail.dynamics.com',
                accept: 'application/json',
                'accept-language': 'en-GB',
                appsessionid: 'OtVVxv4TZOd4WbCSDTkvYH',
                'cache-control': 'no-cache',
                'content-type': 'application/json',
                contenttype: 'application/json',
                'from-keystone': 'true',
                'odata-maxversion': '4.0',
                'odata-version': '4.0',
                oun: 'Y100',
                pragma: 'no-cache',
                prefer: 'return=representation',
                requestid: 'e42018722d257bb82e53ad56d5d963fc/2',
                usersessionid: 'ADCehL+M8YWP9iwro4TOSA'
            }
        };

        return axios(config)
            .then(function(response: any) {
                return response.data.value;
            })
            .catch(function(error: any) {
                console.log('err', error);
            });
    };

    private _updateCheckoutSharedState = (
        isOtherPostCode: boolean,
        isInvalidAddress: boolean,
        isFirstAddress?: boolean,
        loggedInUserNewAddress?: boolean
    ) => {
        this.props.context.actionContext.update(new CheckoutModulesStateShareInput(), {
            notBtPostCode: isOtherPostCode,
            invalidAddressForCartProducts: isInvalidAddress,
            isFirstAddress: isFirstAddress, // Check if it is guest checkout or if loggedIn user enters address for the first time and doesn't have any saved address
            loggedInUserNewAddress: loggedInUserNewAddress // Check if it is loggedIn user and tries to add NEW address for second or onwards time, dont disbale shipping address buttom but delivery options if there is restriction case
        });
    };
    /* VSI Customization - END */

    /**
     * Method get called on select address.
     */
    private readonly _onSelectAddress = async (): Promise<void> => {
        /* VSI Customization - START - 16/04/21 */
        // Check if Bt is not selected on clicking Cancel button, disable the confirm & proceed button
        if (this.addressListSelectedAddress && this._isRestrictedPostCode(this.addressListSelectedAddress)) {
            this._updateCheckoutSharedState(true, true);
        } else {
            this._updateCheckoutSharedState(false, false);
        }
        /* VSI Customization - END */
        this._updateCurrentOperation(AddressOperation.Show, this.addressListSelectedAddress);
        await this._setShippingAddress(this.addressListSelectedAddress);
        await this.updateCartDeliverySpecification();
        this._updateModuleState();
    };

    private editAddressFromList = (selectedAddress?: Address) => {
        let updatedShippingAddress = selectedAddress;
        if (updatedShippingAddress) {
            updatedShippingAddress = {
                ...selectedAddress,
                Email:
                    updatedShippingAddress.Email && updatedShippingAddress.Email !== ''
                        ? updatedShippingAddress.Email
                        : this.props.data.customerInformation.result?.Email
            };
        }
        this._updateCurrentOperation(AddressOperation.Update, updatedShippingAddress);
    };

    /**
     * Method get called on goto add address.
     */
    private readonly _goToAddAddress = (passSelectedAddress?: boolean) => {
        this._setDefaultCountryRegionId();
        if (passSelectedAddress) {
            this._updateCurrentOperation(AddressOperation.Update, this.selectedAddress);
            this.onAddressAddUpdate('ZipCode', this.selectedAddress?.ZipCode || '');
            this._updateModuleState();
        } else {
            this._updateCurrentOperation(AddressOperation.Add);
        }
    };

    /**
     * Method get called on add/update success.
     * @param response - Retail api response.
     */
    private readonly _onAddOrUpdateSuccess = async (response: IAddressResponse): Promise<void> => {
        if (response.customerAddresses) {
            this.customerAddresses = response.customerAddresses;
        }

        if (response.address) {
            this._updateCurrentOperation(AddressOperation.Show, response.address);
            await this._setShippingAddress(response.address);
            this._updateModuleState();
        }
    };

    /**
     * Method get called on set Shipping Address.
     * @param address - Retail Api address result.
     */
    private readonly _setShippingAddress = async (address: Address): Promise<void> => {
        if (this.props.data.checkout.result) {
            const newShippingAddress = { ...address };

            if (address.ThreeLetterISORegionName && !newShippingAddress.TwoLetterISORegionName) {
                newShippingAddress.TwoLetterISORegionName = this.addressFormat.getTwoLetterISORegionName(address.ThreeLetterISORegionName);
            }
            await this.props.data.cart.result?.updateShippingAddress({ newShippingAddress });
            await this.props.data.checkout.result.updateShippingAddress({ newShippingAddress });
        }
    };

    @computed private get shippingAddress(): Address | undefined {
        return this.props.data.checkout.result?.shippingAddress;
    }

    /**
     * Method get called on reset view.
     */
    private readonly _resetView = (): void => {
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                this._updateCurrentOperation(
                    this.addressCommon.isAuthenticatedFlow() ? AddressOperation.List : AddressOperation.Show,
                    this.shippingAddress
                );
                /* VSI-Customization -- START When user clicks the cancel button for adding address update the addressOptions array to empty  */
                this.setState({ addressOptions: [] });
                /* VSI-Customization -- END  */
                break;
            default:
                this._updateCurrentOperation(AddressOperation.Show, this.shippingAddress);
        }
        this._updateModuleState();
    };

    /**
     * Update current operation.
     * @param operation - The address operation.
     * @param selectedAddress - The selected address.
     */
    private readonly _updateCurrentOperation = (operation: AddressOperation, selectedAddress?: Address) => {
        const {
            data: {
                checkoutModulesSharedState: { result: checkoutModulesSharedState }
            }
        } = this.props;
        this.currentOperation = operation;
        this.selectedAddress = selectedAddress;

        if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
            /* VSI Customization - START - 16/04/21 */
            this._updateCheckoutSharedState(false, !!checkoutModulesSharedState?.invalidAddressForCartProducts);
            /* VSI Customization - END */
            this.addUpdateAddress = { ...this.selectedAddress };
            set(this.addUpdateAddress, {
                ['ThreeLetterISORegionName']: this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId
            });
            set(this.addUpdateAddress, {
                ['AddressTypeValue']: this.addUpdateAddress.AddressTypeValue || this.defaultAddressType
            });
        } else if (this.currentOperation === AddressOperation.List) {
            this.addressListSelectedAddress = { ...this.selectedAddress };
        }
    };

    /**
     * Update module state.
     */
    private readonly _updateModuleState = () => {
        if (this.currentOperation === AddressOperation.Show) {
            this.props.moduleState.onReady();
        } else if (this.isUpdating) {
            this.props.moduleState.onPending();
        } else {
            this.props.moduleState.onUpdating();
        }
    };

    // /**
    //  * Method to check if the customer already has the address.
    //  * @param address - The address.
    //  */
    // private readonly _isNewAddress = (address: Address): boolean => {
    //     for (const existingAddress of this.customerAddresses) {
    //         if (
    //             (address.Name ?? '') === existingAddress.Name &&
    //             (address.Street ?? '') === existingAddress.Street &&
    //             (address.StreetNumber ?? '') === existingAddress.StreetNumber &&
    //             (address.City ?? '') === existingAddress.City &&
    //             (address.State ?? '') === existingAddress.State &&
    //             (address.ZipCode ?? '') === existingAddress.ZipCode &&
    //             (address.Phone ?? '') === existingAddress.Phone &&
    //             (address.ThreeLetterISORegionName ?? '') === existingAddress.ThreeLetterISORegionName &&
    //             (address.AddressTypeValue ?? '') === existingAddress.AddressTypeValue
    //         ) {
    //             return false;
    //         }
    //     }

    //     return true;
    // };

    // @action
    // private readonly _setErrorMessage = (errorMessage: string): void => {
    //     this.props.telemetry.error(errorMessage);
    //     this.props.moduleState.setHasError(true);
    //     this.props.moduleState.onUpdating();
    //     this.setState({
    //         errorMessage
    //     });
    // };

    // private readonly _clearError = (): void => {
    //     this.props.moduleState.setHasError(false);
    //     this.setState({
    //         errorMessage: ''
    //     });
    // };
}

export default CheckoutShippingAddress;
